// @flow

import * as React from 'react';
import {useEffect, useState} from 'react';

import Box from 'sail/Box';
import classNames from 'classnames';
import {
  useAccount,
  useAnalytics,
  useArticle,
  useCookies,
  useLocation,
  useSidebar,
} from 'docs/data';
import {useLinkHandler} from 'docs/hooks';
import {initializeGoogleTagManager} from 'docs/util';
import {MobileNavOverlay} from 'docs/shell/mobile/MobileNavOverlay';
import {FadeInPane} from 'docs/shell/mobile/helpers/FadeInPane';
import {DocsFavicon} from 'docs/shell/DocsFavicon';
import {Header} from './header/Header';
import {SidebarFooter} from './sidebar/footer/SidebarFooter';
import {CookieBanner} from './CookieBanner';
import './Shell.scss';
import {OpenGraphMetaTags} from './OpenGraphMetaTags';
import {StripeCluiTerminal} from '../components/contrib/StripeClui/terminal/terminal';

type ShellProps = {|
  children: React.Node,
  sidebar: ?React.Node,
|};

const getClassnameForSidebarState = (
  showSidebar: boolean,
  navigationIsOpen: boolean,
) => {
  if (!showSidebar) {
    return 'Sidebar--hidden';
  }
  return navigationIsOpen ? 'Sidebar--expanded' : 'Sidebar--collapsed';
};

export const Shell = ({children, sidebar}: ShellProps) => {
  const {account} = useAccount();
  const {addToViewHistory, trackView} = useAnalytics();
  const {article, loadArticle, prefetchArticles} = useArticle();
  const {cookies} = useCookies();
  const {location, previousLocation, replaceLocation} = useLocation();
  const {open, show: showSidebar} = useSidebar();
  const [loading, setLoading] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = React.useState(false);

  const handleClick = useLinkHandler();

  useEffect(() => prefetchArticles(), [prefetchArticles]);

  // Load GTM only if we're allowed to set advertising and statistics cookies.
  // TODO (nkohari): This is sort of brute force; see if there's a more elegant way we can do this.
  useEffect(() => {
    cookies
      .areCategoriesAllowed('advertising', 'statistics')
      .then((allowed) => {
        if (allowed) {
          initializeGoogleTagManager();
        }
      });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */ // TODO: use correct dependencies
  }, []);

  // Fires when we update the article due to a location or locale change
  useEffect(() => {
    addToViewHistory(article);
    trackView({article});
    /* eslint-disable-next-line react-hooks/exhaustive-deps */ // TODO: use correct dependencies
  }, [article]);

  // If the user has navigated to a new article, load it from the server.
  useEffect(() => {
    const syncArticleWithLocation = async (path: string) => {
      setLoading(true);
      // Load the article at the new path.
      const nextArticle = await loadArticle(path, {
        locale: account.locale?.code,
        country: account.country?.code,
        merchant: account.merchant?.token,
        version: article.version,
      });

      // If we followed redirects during the loading process, change the location to sync.
      if (nextArticle.path && nextArticle.path !== path) {
        replaceLocation(nextArticle.path);
      }

      setTimeout(() => {
        // TODO(adreyfus): Consider replacing this with framer-motion to animate the Content width
        setLoading(false);
      }, 200);
    };

    // TODO(DOCSPRODUCT-952): We should rethink and test how this works.
    // There is a currently a bug that navigates the user to /undefined in certain cases

    if (
      article.metadata.type === 'error' &&
      decodeURI(article.path) === location.pathname
    ) {
      // If the article is a 404 or 500, the path might not neccesarily equal the location due
      // to uri encoding differences between the browser and the server, so avoid trying to
      // resync the article
      return;
    }

    if (
      // If the user has navigated to a new URL
      article.path !== location.pathname ||
      // Or the user has changed their i18n settings
      article.intl.country !== account.country.code ||
      article.intl.locale !== account.locale.code
    ) {
      // Load the article at the current URL
      syncArticleWithLocation(location.pathname);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */ // TODO: use correct dependencies
  }, [location, account]);

  const handleToggleNavSearch = () => {
    setIsMobileSearchOpen(!isMobileSearchOpen);
  };

  const handleCloseNav = () => {
    setIsMobileSearchOpen(false);
  };

  const isLastOnIntegrationBuilder = previousLocation
    ? previousLocation.isIntegrationBuilder()
    : false;

  return (
    <Box
      className={classNames(
        'Shell',
        loading ? 'Shell-loading' : 'Shell-loaded',
        getClassnameForSidebarState(showSidebar, open),
      )}
      onClick={handleClick}
      flex={{direction: 'column'}}
    >
      <DocsFavicon />
      <OpenGraphMetaTags />
      <Header
        isMobileSearchOpen={isMobileSearchOpen}
        onToggleNavSearch={handleToggleNavSearch}
      />
      <Box flex className="Shell-container">
        {showSidebar && <Box>{sidebar}</Box>}
        <Box
          flexGrow={1}
          style={{
            display: isMobileSearchOpen ? 'block' : 'flex',
            flexFlow: 'column nowrap',
            alignItems: 'stretch',
            justifyContent: 'flex-start',
          }}
        >
          <>
            <FadeInPane
              isVisible={isMobileSearchOpen}
              // Navigating to and from the integration buidler is a special case because we
              // go from a mobile zoom to a browser zoom. In this case we do not want to do
              // a fade out.
              isRemovedWithoutFade={
                (location.isIntegrationBuilder() ||
                  isLastOnIntegrationBuilder ||
                  article.metadata?.type === 'integration-builder') &&
                !isMobileSearchOpen
              }
            >
              <MobileNavOverlay onCloseNav={handleCloseNav} />
            </FadeInPane>
            {children}
          </>
          {!isMobileSearchOpen && account?.flags?.clui_v1_rollout && (
            <StripeCluiTerminal />
          )}
        </Box>
        {!showSidebar && (
          <Box className="SidebarFooter-fixed">
            <SidebarFooter showLabels={false} />
          </Box>
        )}
      </Box>
      <CookieBanner />
    </Box>
  );
};
